/**
 * Mastery level helpers for the TradRep redesign.
 *
 * The backend has 6 canonical mastery levels (sort order 0–5, see
 * seed-mastery-levels.yaml). The UI surfaces the canonical labels in
 * selectors and detail views, but uses short nicknames and a star count
 * in tight spaces like badges and the session-complete modal.
 *
 * See docs/design/tradrep-design-direction.md §6.
 */

export type MasteryDescriptor = {
  /** Backend-canonical sort order (0–5). */
  sortOrder: number;
  /** Short label for tight spaces. */
  shortLabel: string;
  /**
   * Full star count expressed in half-star steps, i.e. 0, 0.5, 1, 1.5, 2, 2.5, 3.
   * 0 = no stars (New), 3 = three full stars (Fluent).
   */
  stars: number;
};

const DESCRIPTORS_BY_SORT_ORDER: readonly MasteryDescriptor[] = [
  { sortOrder: 0, shortLabel: 'New', stars: 0 },
  { sortOrder: 1, shortLabel: 'Rusty', stars: 0.5 },
  { sortOrder: 2, shortLabel: 'Sightread', stars: 1 },
  { sortOrder: 3, shortLabel: 'Learning', stars: 1.5 },
  { sortOrder: 4, shortLabel: 'Solid', stars: 2.5 },
  { sortOrder: 5, shortLabel: 'Fluent', stars: 3 },
];

const CANONICAL_NAME_TO_SORT_ORDER: Readonly<Record<string, number>> = {
  not_started: 0,
  hard_to_sightread: 1,
  good_sightreading: 2,
  halfway_memorized: 3,
  memorized_with_articulation_dynamics: 4,
  performance_ready: 5,
};

const UNKNOWN_DESCRIPTOR: MasteryDescriptor = { sortOrder: 0, shortLabel: 'New', stars: 0 };

/**
 * Resolve a mastery descriptor from the backend's sort order (0–5).
 * Returns the "New" descriptor for out-of-range input.
 */
export function masteryFromSortOrder(sortOrder: number | null | undefined): MasteryDescriptor {
  if (sortOrder == null) return UNKNOWN_DESCRIPTOR;
  return DESCRIPTORS_BY_SORT_ORDER[sortOrder] ?? UNKNOWN_DESCRIPTOR;
}

/**
 * Resolve a mastery descriptor from the backend's canonical name
 * (e.g. "halfway_memorized"). Returns the "New" descriptor for
 * unknown values.
 */
export function masteryFromCanonicalName(name: string | null | undefined): MasteryDescriptor {
  if (!name) return UNKNOWN_DESCRIPTOR;
  const sortOrder = CANONICAL_NAME_TO_SORT_ORDER[name];
  return sortOrder == null ? UNKNOWN_DESCRIPTOR : (DESCRIPTORS_BY_SORT_ORDER[sortOrder] ?? UNKNOWN_DESCRIPTOR);
}

/**
 * All six canonical descriptors in order. Used by selectors.
 */
export function allMasteryDescriptors(): readonly MasteryDescriptor[] {
  return DESCRIPTORS_BY_SORT_ORDER;
}

export type MasteryVisualLevel = 'new' | 'learning' | 'solid' | 'fluent';

export type VisualLevelDescriptor = {
  level: MasteryVisualLevel;
  blurb: string;
};

export const VISUAL_LEVEL_DESCRIPTORS: readonly VisualLevelDescriptor[] = [
  { level: 'new', blurb: 'Just heard it. Have to look up notes.' },
  { level: 'learning', blurb: 'Knows the shape. Bumpy in spots.' },
  { level: 'solid', blurb: 'Plays through clean. Some thinking.' },
  { level: 'fluent', blurb: 'Plays without thinking. Can vary it.' },
];

const SORT_TO_VISUAL: Readonly<Record<number, MasteryVisualLevel>> = {
  0: 'new',
  1: 'learning',
  2: 'learning',
  3: 'solid',
  4: 'solid',
  5: 'fluent',
};

export function masteryVisualLevel(sortOrder: number | null | undefined): MasteryVisualLevel {
  if (sortOrder == null) return 'new';
  return SORT_TO_VISUAL[sortOrder] ?? 'new';
}

const VISUAL_TO_SUBMIT_SORT_ORDER = { new: 0, learning: 2, solid: 4, fluent: 5 } as const;

export function visualLevelToSubmitSortOrder(level: MasteryVisualLevel): 0 | 2 | 4 | 5 {
  return VISUAL_TO_SUBMIT_SORT_ORDER[level];
}
